import React, { useState, useMemo } from 'react';
import { ChevronDown, Check, Package, Calculator } from 'lucide-react';
import { weddingThemes } from '../../data/weddingThemes';

const STATIONERY_CATEGORIES = [
  { id: 'invitations', label: 'Invitations' },
  { id: 'save-the-date', label: 'Save the Date' },
  { id: 'rsvp', label: 'RSVP-kort' },
  { id: 'place-cards', label: 'Place cards' },
  { id: 'table-numbers', label: 'Bordnummer' },
  { id: 'menu', label: 'Menu cards' },
  { id: 'program', label: 'Program / Timeline' },
  { id: 'welcome-sign', label: 'Welcome sign' },
  { id: 'seating-chart', label: 'Bordoversikt-plakat' },
  { id: 'bar-menu', label: 'Bar-/drinkmeny' },
  { id: 'guestbook-sign', label: 'Guest book-signs' },
  { id: 'qr-sign', label: 'QR sign (Guest Camera)' },
  { id: 'photobooth-sign', label: 'Photo booth sign' },
  { id: 'memorial-sign', label: 'Memorial table sign' },
  { id: 'dessert-sign', label: 'Dessert-/kaketable-signs' },
  { id: 'thank-you', label: 'Thank you cards' },
  { id: 'favor-tags', label: 'Gaveetiketter / Favors' },
  { id: 'custom', label: 'Custom signs' },
];

const PRINT_STATUSES = ['Not started', 'Under design', 'Sendt til trykk', 'Done trykt'];

const emptyEntry = () => ({
  design: '', text: '', size: '', quantity: '', material: '',
  vendor: '', estimatedPrice: '', actualPrice: '',
  ordered: false, received: false, printStatus: 'Not started', notes: '',
});

const fmt = (val) => { const n = parseFloat(val); return isNaN(n) ? '' : n.toLocaleString('en-US'); };

export const StationeryPlanner = ({ stationery, onChange, formData }) => {
  const [openSections, setOpenSections] = useState(['invitations']);
  const data = stationery || {};

  const theme = useMemo(() => weddingThemes.find(t => t.id === formData?.bookTheme), [formData?.bookTheme]);

  const toggle = (id) => setOpenSections(p => p.includes(id) ? p.filter(s => s !== id) : [...p, id]);
  const update = (catId, updates) => onChange({ ...data, [catId]: { ...(data[catId] || emptyEntry()), ...updates } });

  const totals = useMemo(() => {
    let est = 0, act = 0, filled = 0, ordered = 0, received = 0;
    STATIONERY_CATEGORIES.forEach(c => {
      const e = data[c.id];
      if (e?.design?.trim() || e?.text?.trim()) filled++;
      if (e?.ordered) ordered++;
      if (e?.received) received++;
      const ev = parseFloat(e?.estimatedPrice); if (!isNaN(ev)) est += ev;
      const av = parseFloat(e?.actualPrice); if (!isNaN(av)) act += av;
    });
    return { est, act, filled, ordered, received };
  }, [data]);

  return (
    <div data-testid="stationery-planner" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Brevpapir & Skilt</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Plan all printed materials and signs for the wedding</p>
        </div>
        <span className="text-[10px] font-sans text-stone-500">{totals.filled} / {STATIONERY_CATEGORIES.length}</span>
      </div>

      {/* Theme reference */}
      {theme && (
        <div className="bg-white rounded-xl border border-stone-200/80 p-3" data-testid="stationery-theme-ref">
          <p className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-2">Design fra {theme.name}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {theme.colors.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-4 h-4 rounded-full border border-stone-200/50" style={{ background: c }} />
                <span className="text-[8px] font-sans text-stone-400">{theme.colorNames?.[i] || c}</span>
              </div>
            ))}
            <span className="text-[9px] font-sans text-stone-400 ml-2">Font: {theme.fontPair}</span>
          </div>
        </div>
      )}

      {/* Budget summary */}
      {(totals.est > 0 || totals.act > 0) && (
        <div className="bg-white rounded-xl border border-stone-200/80 p-3 flex items-center gap-4" data-testid="stationery-budget">
          <Calculator className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
          <div className="flex-1 flex items-center gap-4 text-[10px] font-sans flex-wrap">
            <div><span className="text-stone-400">Estimert: </span><span className="font-bold text-stone-700">{fmt(totals.est)} kr</span></div>
            {totals.act > 0 && <div><span className="text-stone-400">Faktisk: </span><span className={`font-bold ${totals.act > totals.est ? 'text-red-600' : 'text-green-600'}`}>{fmt(totals.act)} kr</span></div>}
            <div><span className="text-stone-400">Bestilt: </span><span className="font-bold text-stone-700">{totals.ordered}</span></div>
            <div><span className="text-stone-400">Mottatt: </span><span className="font-bold text-stone-700">{totals.received}</span></div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-2" data-testid="stationery-categories">
        {STATIONERY_CATEGORIES.map(cat => {
          const isOpen = openSections.includes(cat.id);
          const entry = data[cat.id] || emptyEntry();
          const hasFill = entry.design?.trim() || entry.text?.trim();

          return (
            <div key={cat.id} className={`rounded-xl border transition-all ${hasFill ? 'border-primary/20 bg-primary/[0.02]' : 'border-stone-200/80 bg-white'}`}
              data-testid={`stationery-cat-${cat.id}`}>
              <button onClick={() => toggle(cat.id)} className="flex items-center w-full px-4 py-2.5 text-left gap-3">
                <Package className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                <span className="flex-1 text-[11px] font-sans font-semibold text-stone-700">{cat.label}</span>
                {entry.ordered && <Check className="w-3 h-3 text-green-500" />}
                {hasFill && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                {entry.printStatus !== 'Not started' && (
                  <span className="text-[8px] font-sans px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500">{entry.printStatus}</span>
                )}
                <ChevronDown className={`w-3 h-3 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <div className="px-4 pb-3 space-y-2.5 border-t border-stone-100 pt-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Design / Stil" value={entry.design} onChange={v => update(cat.id, { design: v })} placeholder="e.g. minimalistisk, klassisk" />
                    <Field label="Tekst / Budskap" value={entry.text} onChange={v => update(cat.id, { text: v })} placeholder="Hovedtekst or referanse" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Size" value={entry.size} onChange={v => update(cat.id, { size: v })} placeholder="e.g. A5, 10x15cm" />
                    <Field label="Number of" value={entry.quantity} onChange={v => update(cat.id, { quantity: v })} placeholder="0" type="number" />
                    <Field label="Papir / Materiale" value={entry.material} onChange={v => update(cat.id, { material: v })} placeholder="e.g. bomull, akryl" />
                  </div>
                  <Field label="Vendor" value={entry.vendor} onChange={v => update(cat.id, { vendor: v })} placeholder="Trykkeri or designer" />
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Estimated price (kr)" value={entry.estimatedPrice} onChange={v => update(cat.id, { estimatedPrice: v })} type="number" />
                    <Field label="Actual price (kr)" value={entry.actualPrice} onChange={v => update(cat.id, { actualPrice: v })} type="number" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <SelectField label="Printstatus" value={entry.printStatus} onChange={v => update(cat.id, { printStatus: v })} options={PRINT_STATUSES} />
                    <Toggle label="Bestilt" checked={entry.ordered} onChange={v => update(cat.id, { ordered: v })} />
                    <Toggle label="Mottatt" checked={entry.received} onChange={v => update(cat.id, { received: v })} />
                  </div>
                  <Field label="Notes" value={entry.notes} onChange={v => update(cat.id, { notes: v })} placeholder="Ekstra info..." textarea />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Shared field helpers
const Field = ({ label, value, onChange, placeholder, type, textarea }) => (
  <div>
    <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    {textarea ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2}
        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none resize-none" />
    ) : (
      <input type={type || 'text'} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''}
        className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <select value={value || options[0]} onChange={e => onChange(e.target.value)}
      className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none bg-white">
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <div>
    <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <button onClick={() => onChange(!checked)}
      className={`flex items-center gap-1.5 text-[10px] font-sans font-medium px-2.5 py-1.5 rounded-lg border w-full transition-all ${
        checked ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
      }`}>
      {checked && <Check className="w-3 h-3" />}
      {checked ? 'Ja' : 'Nei'}
    </button>
  </div>
);
